// Temple University: public college page (#2652, round 2), official facts
// only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Temple's Common Data Set 2025-2026 (the "26-27 Class Rank Update" PDF
//    that Institutional Research and Assessment links from its Compliance
//    Reports page): C1 applicants/admits/enrollees for Fall 2025 with the
//    residency table (its "unknown" column is 0), C8 test policy for Fall
//    2027, C21/C22 plans.
//  - Temple Undergraduate Admissions: First-Year Students ("2027 Application
//    is now Open": deadlines, Early Action, what Temple considers, the
//    audition and portfolio majors) and Test Optional (the policy and FAQ).
//    Temple publishes an Early Action decision date (January 10) and says
//    Regular Decision applicants hear "shortly after" the application is
//    complete.
//  - Temple Student Financial Services, Cost of Attendance: the 2026-27
//    undergraduate budget. Tuition differs by school or college; the page
//    gives College of Liberal Arts and Fox School of Business examples for
//    PA residents and non-residents. The College of Liberal Arts columns are
//    shown, with the Fox totals in the note. The CDS G1 tuition is a
//    weighted average across schools and its G5 leaves other expenses blank,
//    so the financial aid office's budget is used.
//  - College Scorecard API, school id 216339 (net price by family income).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Temple Common Data Set 2025-2026',
        url: 'https://ira.temple.edu/sites/ira/files/media/document/CDS%202025-26_Temple%20University_26-27_Class-Rank-Update.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Temple Admissions, First-Year Students',
        url: 'https://admissions.temple.edu/apply/first-year-students',
        read: '2026-10-01',
    },
    testOptional: {
        label: 'Temple Admissions, Test Optional',
        url: 'https://admissions.temple.edu/apply/first-year-students/test-optional',
        read: '2026-10-01',
    },
    costs: {
        label: 'Temple Student Financial Services, Cost of Attendance',
        url: 'https://sfs.temple.edu/eligibility/your-financial-aid-offer/cost-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?216339-Temple-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'temple-university',
    kbId: 'temple_university',
    name: 'Temple University',
    shortName: 'Temple',
    location: 'Philadelphia, PA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (temple.edu); decoration only.
    branding: {
        primary: '#A41E35',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/temple_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 42824,
        admitted: 36922,
        enrolled: 5303,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 14512, admitted: 12641 },
            { group: 'Out-of-state', applied: 24878, admitted: 21812 },
            { group: 'International', applied: 3434, admitted: 2469 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Temple\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Temple has no early decision plan. Its Early Action plan is not restrictive, and the Common Data Set gives its dates but no early action counts, so it gives no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Temple with one Common App. Some majors ask for more:',
        rows: [
            { school: 'Boyer College of Music and Dance, Tyler School of Art and Architecture, School of Theater, Film and Media Arts', requirement: 'Some majors may require an audition, interview or portfolio. Check your intended major\'s requirements with the school or college. You may schedule an audition and submit a portfolio before you finish your application.' },
        ],
        note: 'The Sullivan Honors College needs no separate application: every applicant is considered with the regular application.',
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'By January 10', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'Shortly after your application is complete', binding: false },
        ],
        note: 'The application opens August 1 on the Common App, which Temple uses exclusively, and costs $55 unless you qualify for a waiver. Students at U.S. high schools must self-report grades through STARS to complete the application; a transcript alone does not. Early Action is not binding. The FAFSA priority deadline is February 1 and the enrollment deposit is due May 1. From October through March, a Regular Decision can take more than eight weeks.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Temple is test-optional and accepts the SAT and ACT. You choose on the Common App whether to send scores or apply test-optional, and the choice cannot change once you have a decision. If you apply test-optional, Temple does not consider scores even if they were sent. Test-optional applicants are still considered for academic scholarships and the Honors Program.',
        source: 'testOptional',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees (College of Liberal Arts)', amounts: [21474, 37698] },
            { item: 'Housing', amounts: [14084, 14084] },
            { item: 'Food', amounts: [5552, 5552] },
            { item: 'Books', amounts: [1330, 1330] },
            { item: 'Transportation', amounts: [2296, 2296] },
            { item: 'Other (personal, miscellaneous)', amounts: [3424, 3424] },
        ],
        totals: [48160, 64384],
        note: 'Temple\'s anticipated 2026-27 budget for a College of Liberal Arts student; in-state means a Pennsylvania resident. Tuition differs by school or college: in the Fox School of Business, tuition and fees are $26,322 in-state and $46,866 out-of-state, for totals of $53,008 and $73,552.',
        source: 'costs',
        netPrice: {
            average: 23058,
            byIncome: [
                { income: '$0 to $30,000', amount: 19047 },
                { income: '$30,001 to $48,000', amount: 18541 },
                { income: '$48,001 to $75,000', amount: 20684 },
                { income: '$75,001 to $110,000', amount: 24345 },
                { income: 'Over $110,000', amount: 29206 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Temple\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
